import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Autocomplete } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon issue (ensure icons load when this component is used standalone)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Suggestion {
  display_name: string;
  lat: string;
  lon: string;
}

interface LocationPickerProps {
  lat: number;
  lng: number;
  zoom?: number;
  searchSuffix?: string;
  onLocationChange: (lat: number, lng: number, googleMapsLink?: string) => void;
}

const DEFAULT_ZOOM = 15;

const LocationPicker: React.FC<LocationPickerProps> = ({ lat, lng, zoom = DEFAULT_ZOOM, searchSuffix = '', onLocationChange }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom,
      zoomControl: true,
    });

    // Multiple basemap options for better selection
    const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    });

    const esri = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles © Esri',
      maxZoom: 19,
    });

    const positron = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap, &copy; CartoDB',
      maxZoom: 19,
    });

    // Default OSM
    osm.addTo(map);

    // Layer switcher control
    const baseMaps = {
      'OpenStreetMap': osm,
      'Satellite (Esri)': esri,
      'Carto Positron': positron,
    };
    L.control.layers(baseMaps, undefined, { position: 'topright', collapsed: false }).addTo(map);

    const marker = L.marker([lat, lng], { draggable: true }).addTo(map);

    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      onLocationChange(pos.lat, pos.lng, `https://www.google.com/maps?q=${pos.lat},${pos.lng}`);
    });

    map.on('click', (e: L.LeafletMouseEvent) => {
      marker.setLatLng(e.latlng);
      onLocationChange(e.latlng.lat, e.latlng.lng, `https://www.google.com/maps?q=${e.latlng.lat},${e.latlng.lng}`);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Update marker when lat/lng props change
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.setView([lat, lng], mapInstanceRef.current.getZoom(), { animate: true });
      } catch (error) {
        console.warn('Could not set map view:', error);
      }
    }
  }, [lat, lng]);

  // Debounced search to Nominatim (prefer Lebanon results)
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const q = encodeURIComponent(query + (searchSuffix ? ` ${searchSuffix}` : ''));
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${q}`;
        const res = await fetch(url, { signal: controller.signal, headers: { 'Accept-Language': 'en' } });
        const data: Suggestion[] = await res.json();
        setSuggestions(data);
      } catch (err) {
        if ((err as any).name !== 'AbortError') console.error('Nominatim search error', err);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [query]);

  const handleSelect = async (selection: Suggestion | string | null) => {
    if (!selection) return;

    try {
      let selectedLat: number | null = null;
      let selectedLng: number | null = null;

      if (typeof selection === 'string') {
        // User entered a free-text value (freeSolo). Try geocoding the string.
        const q = encodeURIComponent(selection + (searchSuffix ? ` ${searchSuffix}` : ''));
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&q=${q}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data: Suggestion[] = await res.json();
        if (!data || data.length === 0) {
          console.warn('[LocationPicker] No geocoding results for:', selection);
          return;
        }
        selectedLat = parseFloat(data[0].lat);
        selectedLng = parseFloat(data[0].lon);
      } else {
        selectedLat = parseFloat(selection.lat);
        selectedLng = parseFloat(selection.lon);
      }

      if (Number.isNaN(selectedLat) || Number.isNaN(selectedLng)) {
        console.warn('[LocationPicker] Received invalid coordinates from selection:', selection);
        return;
      }

      // Update marker and map safely
      if (markerRef.current) markerRef.current.setLatLng([selectedLat, selectedLng]);
      if (mapInstanceRef.current) mapInstanceRef.current.setView([selectedLat, selectedLng], DEFAULT_ZOOM);

      onLocationChange(selectedLat, selectedLng, `https://www.google.com/maps?q=${selectedLat},${selectedLng}`);
    } catch (err) {
      console.error('[LocationPicker] Error handling selection:', err);
    }
  };

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={suggestions}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.display_name)}
        filterOptions={(opts) => opts}
        loading={isSearching}
        onInputChange={(_, value) => setQuery(value)}
        onChange={(_, value) => handleSelect(value as Suggestion)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search location (type address, town or landmark)"
            placeholder="e.g., Beirut Corniche, Hamra, Saida"
            fullWidth
          />
        )}
      />

      <Box
        ref={mapRef}
        sx={{ height: { xs: 260, sm: 350, md: 420 }, width: '100%', mt: 1, borderRadius: 1, overflow: 'hidden', '& .leaflet-container': { height: '100%', width: '100%' } }}
      />
    </Box>
  );
};

export default LocationPicker;
